import { AppSettings, Environment, ConfigFactory } from '@/types/settings';

/**
 * Local/Development configuration
 */
const localSettings: AppSettings = {
  APP_NAME: 'PagerDuty Onboarding Assistant',
  BASE_PATH: '/',
  ENVIRONMENT: 'development',
  MFE_REF: 'template-mfe-ref',
  APP_SHELL_REF: 'portal-app-shell-ref',
  // API_BASE_URL: 'https://test-services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs', // fallback
  API_BASE_URL: 'http://localhost:8000',
  //API_BASE_URL: 'https://test-services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  MCP_SERVER_URL: 'http://localhost:80/csg-innv-mcp-server-ecs',
  //MCP_SERVER_URL: 'https://test-services.csg.pagerduty.com/csg-innv-mcp-server-ecs',
  TOKEN_STORAGE_KEY: 'access_token',
};

/**
 * Test environment configuration
 */
const testSettings: AppSettings = {
  APP_NAME: 'csg-innv-agent-ui-cf',
  BASE_PATH: '/',
  ENVIRONMENT: 'test',
  MFE_REF: 'template-mfe-ref',
  APP_SHELL_REF: 'portal-app-shell-ref',
  API_BASE_URL: 'https://test-services.csg.pagerduty.com/fde-opt-clean-asst-agent-ecs', // fallback
  ONBOARDING_AGENT_URL: 'https://test-services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  CLEANUP_AGENT_URL: 'https://test-services.csg.pagerduty.com/fde-opt-clean-asst-agent-ecs',
  MCP_SERVER_URL: 'https://test-services.csg.pagerduty.com/fde-opt-clean-asst-mcp-ecs',
  TOKEN_STORAGE_KEY: 'access_token',
};

/**
 * Staging environment configuration
 */
const stagingSettings: AppSettings = {
  APP_NAME: 'csg-innv-agent-ui-cf',
  BASE_PATH: '/',
  ENVIRONMENT: 'staging',
  MFE_REF: 'template-mfe-ref',
  APP_SHELL_REF: 'portal-app-shell-ref',
  API_BASE_URL: 'https://staging-services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  ONBOARDING_AGENT_URL: 'https://staging-services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  CLEANUP_AGENT_URL: 'https://staging-services.csg.pagerduty.com/csg-innv-ocu-asst-client-ecs',
  MCP_SERVER_URL: 'https://staging-services.csg.pagerduty.com/csg-innv-mcp-server-ecs',
  TOKEN_STORAGE_KEY: 'access_token',
};

/**
 * Production environment configuration
 */
const productionSettings: AppSettings = {
  APP_NAME: 'csg-innv-agent-ui-cf',
  BASE_PATH: '/',
  ENVIRONMENT: 'production',
  MFE_REF: 'template-mfe-ref',
  APP_SHELL_REF: 'portal-app-shell-ref',
  API_BASE_URL: 'https://services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  ONBOARDING_AGENT_URL: 'https://services.csg.pagerduty.com/csg-innv-onb-asst-client-ecs',
  CLEANUP_AGENT_URL: 'https://services.csg.pagerduty.com/csg-innv-ocu-asst-client-ecs',
  MCP_SERVER_URL: 'https://services.csg.pagerduty.com/csg-innv-mcp-server-ecs',
  TOKEN_STORAGE_KEY: 'access_token',
};

/**
 * Configuration factory implementation
 */
class AppConfigFactory implements ConfigFactory {
  private readonly configurations: Record<Environment, AppSettings> = {
    development: localSettings,
    test: testSettings,
    staging: stagingSettings,
    production: productionSettings,
  };

  getSettings(environment: Environment): AppSettings {
    const settings = this.configurations[environment];
    if (!settings) {
      throw new Error(`Invalid environment: ${environment}`);
    }

    if (!this.validateSettings(settings)) {
      throw new Error(`Invalid configuration for environment: ${environment}`);
    }

    return { ...settings };
  }

  getCurrentSettings(): AppSettings {
    const currentEnv = this.getCurrentEnvironment();
    return this.getSettings(currentEnv);
  }

  validateSettings(settings: AppSettings): boolean {
    const requiredFields: (keyof AppSettings)[] = [
      'APP_NAME',
      'BASE_PATH',
      'ENVIRONMENT',
      'MFE_REF',
      'APP_SHELL_REF',
      'API_BASE_URL',
      'MCP_SERVER_URL',
      'TOKEN_STORAGE_KEY',
    ];

    return requiredFields.every((field) => {
      const value = settings[field];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });
  }

  private getCurrentEnvironment(): Environment {
    // Use BUILD_MODE injected at build time by Vite's define
    const buildMode = process.env.BUILD_MODE as Environment | undefined;

    if (buildMode) {
      return buildMode;
    }

    // Fallback to import.meta.env.MODE for Vite development server
    const viteMode = import.meta.env?.MODE;
    if (viteMode) {
      return viteMode as Environment;
    }

    // Final fallback to NODE_ENV
    const nodeEnv = process.env.NODE_ENV;

    // Map NODE_ENV to our environment types
    switch (nodeEnv) {
      case 'development':
        return 'development';
      case 'test':
        return 'test';
      case 'staging':
        return 'staging';
      case 'production':
        return 'production';
      default:
        // Default to development for unknown environments
        return 'development';
    }
  }
}

// Create singleton instance
const configFactory = new AppConfigFactory();

// Export the current configuration
export const appConfig = configFactory.getCurrentSettings();

// Export the factory for testing and advanced usage
export { configFactory, localSettings, testSettings, stagingSettings, productionSettings };
